import * as Sentry from '@sentry/vue'

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {}
}

function getString(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

function shouldIgnoreUnhandledTimeout(event: Record<string, unknown>): boolean {
  const exception = asRecord(event.exception)
  const values = Array.isArray(exception.values) ? exception.values : []

  return values.some((entry) => {
    const parsedEntry = asRecord(entry)
    const type = getString(parsedEntry.type)
    const value = getString(parsedEntry.value)
    const mechanismType = getString(asRecord(parsedEntry.mechanism).type)
    const hasStacktrace = Object.keys(asRecord(parsedEntry.stacktrace)).length > 0

    return (
      type === 'UnhandledRejection' &&
      mechanismType.includes('onunhandledrejection') &&
      !hasStacktrace &&
      value.startsWith('Non-Error promise rejection captured with value: Request timeout ')
    )
  })
}

export default defineNuxtPlugin((nuxtApp) => {
  const router = useRouter()
  const {
    public: { sentry },
  } = useRuntimeConfig()

  if (!sentry.dsn) return

  Sentry.init({
    app: nuxtApp.vueApp,
    dsn: sentry.dsn,
    environment: sentry.environment,
    release: sentry.release,
    integrations: [Sentry.browserTracingIntegration({router})],
    beforeSend: (event, hint) => {
      if (shouldIgnoreUnhandledTimeout(asRecord(event))) {
        return null
      }

      if (hint.originalException && hint.originalException.statusCode && hint.originalException.statusCode === 404) {
        return null
      }
      return event
    },
  })

  nuxtApp.hook('app:error', (error) => {
    const parsedError = asRecord(error)
    const parsedData = asRecord(parsedError.data)
    const statusCode = Number(parsedError.statusCode)
    const statusMessage = getString(parsedError.statusMessage)
    const errorMessage = getString(parsedError.message)
    const reason = getString(parsedData.reason) || statusMessage || errorMessage || 'Unknown internal server error'

    Sentry.withScope((scope) => {
      if (Number.isFinite(statusCode) && statusCode > 0) {
        scope.setTag('status_code', String(statusCode))
      }

      scope.setExtra('reason', reason)
      scope.setExtra('statusMessage', statusMessage)
      scope.setExtra('errorMessage', errorMessage)

      if (statusCode === 500) {
        Sentry.captureException(new Error(`HTTP 500: ${reason}`))
        return
      }

      if (error instanceof Error) {
        Sentry.captureException(error)
        return
      }

      Sentry.captureException(new Error(reason))
    })
  })
})
